import React from 'react';
import { PostInsights, SocialMember } from '@frontend/gql/social/graphql';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHeart, faEye, faCalendar, faStar } from '@fortawesome/free-solid-svg-icons';
import { Badge } from '@frontend/shadcn/components/ui/badge';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@frontend/shadcn/components/ui/tooltip';
import { useSocialMediaThumbnail } from '@frontend/applications/Social/hooks/useSocialMediaThumbnail';
import { formatDate } from '@frontend/applications/Social/utils/shared/date-helpers';
import {
  PostCard,
  PostCardMedia,
  PostCardSkeleton,
  PostCardBackground,
  PostCardContent,
  PostCardImageContainer,
  PostCardImageContent,
  PostCardNetworkIcon,
  PostCardImage,
  PostCardPlaceholder,
  PostCardGradient,
  PostCardHeader,
  PostCardAvatar,
  PostCardAvatarImage,
  PostCardAvatarFallback,
  PostCardAvatarName,
  PostCardFooter,
  PostCardActions,
  PostCardStats,
  PostCardStat,
  PostCardAvatarContainer,
  PostCardHeaderRow,
  PostCardStatText,
} from '@frontend/applications/Social/components/post-card';
import { useTabNavigation } from '../../hooks/useTabNavigation';
import { CrossTabNavigationOptions } from '../../hooks';

type TopPostCardProps = {
  post?: PostInsights;
  onClick?: () => void;
  additionalParams?: CrossTabNavigationOptions['additionalParams'];
};

const TopPostCard = React.forwardRef<HTMLDivElement, TopPostCardProps>(({ post, onClick, additionalParams }, ref) => {
  const { imageUrl, isLoading, onImageError } = useSocialMediaThumbnail({
    postUrl: post?.postPermalink ?? '',
    postImageUrl: post?.postImageUrl ?? '',
  });

  const { handleCrossTabNavigation } = useTabNavigation();

  // If `post` is optional, bail out immediately.
  if (!post) return null;

  const member: Partial<SocialMember> = post.member ?? {};
  const memberName = member.name ?? '';
  const profilePicture = member.profilePicture ?? '';

  // Parse tags from post.postCaption
  const tags = post.postCaption?.match(/#([\w_]+)/g) || [];
  const tagsVisible = tags.slice(0, 3);

  const showLoading = isLoading;
  const showImage = imageUrl && !showLoading;
  const showPlaceholder = !showImage && !showLoading;

  const handleClick = (e: React.SyntheticEvent) => {
    e.preventDefault();

    if (post?.entityId) {
      // Navigate to posts tab with postId parameter while preserving existing filters
      handleCrossTabNavigation({
        targetTab: 'posts',
        sourceTab: 'summary',
        additionalParams: {
          postId: post.entityId,
          ...(additionalParams ?? {}),
        },
      });
    } else if (post?.postPermalink) {
      // Fallback to opening external link if no entity ID
      window.open(post.postPermalink, '_blank', 'noopener,noreferrer');
    }

    onClick?.();
  };

  return (
    <PostCard
      ref={ref}
      onClick={handleClick}
      className="cursor-pointer transition-transform hover:scale-[1.02] active:scale-[0.98]"
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick(e);
        }
      }}
    >
      <PostCardMedia>
        {showLoading && <PostCardSkeleton />}
        {showImage && (
          <>
            <PostCardBackground imageUrl={imageUrl} />
            <PostCardImageContainer>
              <PostCardImageContent>
                <PostCardImage src={imageUrl} onError={onImageError} alt="Post preview" />
              </PostCardImageContent>
            </PostCardImageContainer>
          </>
        )}
        {showPlaceholder && <PostCardPlaceholder />}
        <PostCardGradient />
      </PostCardMedia>

      <PostCardContent>
        <PostCardHeader>
          <PostCardHeaderRow>
            <PostCardAvatarContainer>
              <PostCardAvatar networkIcon={<PostCardNetworkIcon network={post.entityNetwork} />}>
                <PostCardAvatarImage src={profilePicture} alt={memberName} />
                <PostCardAvatarFallback name={memberName} />
              </PostCardAvatar>
              <PostCardAvatarName>{memberName}</PostCardAvatarName>
            </PostCardAvatarContainer>
          </PostCardHeaderRow>
        </PostCardHeader>

        <div className="flex-grow" />

        <PostCardFooter>
          {/* Boost Eligible Badge */}
          {post.advertisableMediaPermissions?.some((permission) => permission?.hasPermissions) && (
            <div className="flex justify-start">
              <Badge variant="secondary" className="bg-green-4 text-white text-xs px-2 py-1 flex items-center gap-1">
                <FontAwesomeIcon icon={faStar} className="h-3 w-3" />
                Boost Eligible
              </Badge>
            </div>
          )}
          {/* Tags Row */}
          {tags.length > 0 && (
            <div className="flex flex-wrap items-center gap-1">
              {tagsVisible.map((tag) => (
                <Badge key={tag} variant="outline" className="text-xs px-2 py-1 bg-grey-2 text-grey-9 border-grey-4">
                  {tag}
                </Badge>
              ))}
              {tagsVisible.length < tags.length && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <Badge variant="outline" className="text-xs px-2 py-1 bg-grey-2 text-grey-9 border-grey-4">
                        +{tags.length - tagsVisible.length}
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{tags.slice(tagsVisible.length).join(', ')}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          )}
          {/* Date and Stats Row */}
          <PostCardActions>
            {/* Date posted */}
            <PostCardStatText
              icon={<FontAwesomeIcon icon={faCalendar} className="h-4 w-4" />}
              text={formatDate(post.postDate)}
            />

            {/* Stats */}
            <PostCardStats>
              {/* Always show impressions (views in design) */}
              {post.total?.impressions != null && (
                <PostCardStat
                  icon={<FontAwesomeIcon icon={faEye} className="h-4 w-4" />}
                  value={post.total.impressions}
                />
              )}
              {/* Show engagement on sm+ screens */}
              {post.total?.engagements != null && (
                <PostCardStat
                  icon={<FontAwesomeIcon icon={faHeart} className="h-4 w-4" />}
                  value={post.total.engagements}
                  className="hidden sm:flex"
                />
              )}
            </PostCardStats>
          </PostCardActions>
        </PostCardFooter>
      </PostCardContent>
    </PostCard>
  );
});

TopPostCard.displayName = 'TopPostCard';

export default TopPostCard;
